/* eslint-disable no-unused-vars */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Linkedin, Twitter, Mail, Phone } from 'lucide-react';
import team from "../Assets/team.jpeg";
import eng from "../Assets/eng.jpg";
import work from "../Assets/work.jpg";
import quility from "../Assets/eng2.jpg";
import teamwork from "../Assets/team15.jpg";

const teamMembers = [
  {
    name: "Courage Makonese",
    position: "Principal Architect & Founder",
    description: "Visionary architect with 10+ years of experience leading innovative design projects across Zimbabwe.",
    image: team,
  },
  {
    name: "Engineering Team Lead",
    position: "Senior Structural Engineer",
    description: "Expert in structural analysis and design, ensuring every project meets the highest safety standards.",
    image: eng,
  },
  {
    name: "Design Team",
    position: "Architecture & Design",
    description: "Creative professionals dedicated to crafting functional and aesthetically stunning spaces.",
    image: work,
  },
  {
    name: "Construction Team",
    position: "Site Operations",
    description: "Skilled builders and project managers bringing blueprints to life with precision and care.",
    image: teamwork,
  },
];

const stats = [
  { value: '200+', label: 'Projects Completed' },
  { value: '50+', label: 'Team Members' },
  { value: '10+', label: 'Years Experience' },
  { value: '100%', label: 'Client Satisfaction' },
];

export const TeamPage = ({ title = "Our Team" }) => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = title;
  }, [title]);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative py-24 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-yellow-400/20 rounded-full filter blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full filter blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 text-center z-10">
          <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.8 }}>
            <div className="w-20 h-1 bg-yellow-400 rounded-full mx-auto mb-6"></div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">Meet Our <span className="text-yellow-400">Expert Team</span></h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">Dedicated professionals committed to bringing your vision to life</p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-800 rounded-full filter blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-400 rounded-full filter blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="h-1 w-12 bg-yellow-400"></div>
              <span className="text-blue-800 font-semibold">Our People</span>
              <div className="h-1 w-12 bg-yellow-400"></div>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Leadership & Key Personnel</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teamMembers.map((member, i) => (
              <motion.div
                key={i}
                initial={{ y: 50, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -8 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden group"
              >
                <div className="relative h-64 overflow-hidden">
                  <img src={member.image} alt={member.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-blue-800 mb-1">{member.name}</h3>
                  <p className="text-yellow-600 font-medium text-sm mb-3">{member.position}</p>
                  <p className="text-gray-600 text-sm">{member.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="text-center p-8 bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg border border-gray-100">
                <div className="text-4xl font-extrabold text-blue-800 mb-2">{stat.value}</div>
                <div className="text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gradient-to-r from-blue-900 to-blue-800 text-center">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Want to Join Our Team?</h2>
          <p className="text-blue-200 mb-8 text-lg">We're always looking for talented individuals to help us build the future.</p>
          <button onClick={() => navigate('/careers')} className="inline-flex items-center gap-2 px-8 py-4 bg-yellow-400 text-blue-900 font-bold rounded-xl hover:bg-yellow-300 transition-colors">
            View Open Positions <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </section>
    </main>
  );
};
